// AgentsPage — 2-pane workspace browser. Left rail lists the workspace's
// agents; clicking a row swaps the center pane to that agent's detail view.
// Mirrors the layout pattern from EvaluationTemplatesPage / GraderPage.

import {
  Badge,
  Body1,
  Button,
  Spinner,
  Tab,
  TabList,
  mergeClasses,
} from '@fluentui/react-components';
import { Add24Filled, Add24Regular, bundleIcon } from '@fluentui/react-icons';
import type { ReactElement } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMatch, useNavigate, useOutlet, useParams } from 'react-router';

import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import {
  DataLakeEntitlementGuidance,
  isDataLakeEntitlementRequiredMessage,
} from '../../features/private-workspaces/index.js';
import { useAgents } from '../../hooks/useAgents/useAgents.js';
import { useWorkspaces } from '../../hooks/useWorkspaces/useWorkspaces.js';
import { getAgentDisplayName } from '../../types/agent.js';
import type { Agent } from '../../types/agent.js';

import { AgentDetailView } from './AgentDetailView/AgentDetailView.js';
import type { AgentFormOutletContext } from './AgentForm/AgentForm.js';
import { useStyles } from './AgentsPage.style.js';
import { useAgentListVersions } from './useAgentListVersions.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const AddIcon = bundleIcon(Add24Filled, Add24Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

type FilterValue = 'all' | 'published' | 'draft';

const MIN_RAIL_WIDTH = 240;
const MAX_RAIL_WIDTH = 600;
const clampRailWidth = (value: number) =>
  Math.max(
    MIN_RAIL_WIDTH,
    Math.min(MAX_RAIL_WIDTH, window.innerWidth - 400, value),
  );

interface RowProps {
  agent: Pick<Agent, 'name' | 'displayName' | 'spec'>;
  version?: number;
  draft: boolean;
  selected: boolean;
  onSelect: (name: string) => void;
}

function AgentRow({ agent, version, draft, selected, onSelect }: RowProps) {
  const styles = useStyles();
  const displayName = getAgentDisplayName(agent);
  const type =
    agent.spec?.agentType === 'AgentHttpProxySpec'
      ? 'HTTP'
      : agent.spec?.agentType === 'GraderAsAgentSpec'
        ? 'Grader'
        : agent.spec?.agentType === 'AgentLlmSpec'
          ? 'LLM'
          : null;

  return (
    <button
      type="button"
      className={mergeClasses(styles.row, selected && styles.rowSelected)}
      onClick={() => onSelect(agent.name)}
      aria-pressed={selected}
      aria-label={`Open agent ${displayName}`}
    >
      <div className={styles.rowNameLine}>
        <span className={styles.rowName} title={displayName}>
          {displayName}
        </span>
        {type !== null && <Badge size="small">{type}</Badge>}
      </div>
      <span className={styles.rowMeta}>
        <span className={styles.rowSlug} title={agent.name}>
          {agent.name}
        </span>
        <span className={styles.rowVersion}>
          {draft && 'Draft'}
          {version !== undefined &&
            `${draft ? ' · ' : ''}v${String(version)}${draft ? ' published' : ''}`}
        </span>
      </span>
    </button>
  );
}

export function AgentsPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { workspaceName: workspaceNameParam } = useParams<{
    workspaceName: string;
  }>();
  const { activeName } = useWorkspaceContext();

  // When the user switches workspace via the switcher, navigate to the new
  // workspace's agents URL.
  useEffect(() => {
    if (activeName && workspaceNameParam && activeName !== workspaceNameParam) {
      void navigate(`/workspaces/${encodeURIComponent(activeName)}/agents`, {
        replace: true,
      });
    }
  }, [activeName, workspaceNameParam, navigate]);

  const { activeWorkspace, isLoading: workspacesLoading } = useWorkspaces();
  const workspaceName = activeWorkspace?.name ?? null;

  const basePath = `/workspaces/${encodeURIComponent(workspaceNameParam!)}/agents`;

  const isCreating = useMatch('/workspaces/:workspaceName/agents/new') !== null;
  const detailMatch = useMatch('/workspaces/:workspaceName/agents/:agentName');
  const routeAgentName =
    detailMatch?.params.agentName !== undefined &&
    detailMatch.params.agentName !== 'new'
      ? detailMatch.params.agentName
      : null;

  const { agents, isLoading, error, refresh, upsertAgent } = useAgents({
    workspaceName,
  });
  const agentNames = useMemo(() => agents.map((a) => a.name), [agents]);
  const {
    latestVersions,
    isLoading: versionsLoading,
    error: versionsError,
    refresh: refreshVersions,
  } = useAgentListVersions(workspaceName, agentNames);

  const [filter, setFilter] = useState<FilterValue>('all');
  const [railWidth, setRailWidth] = useState(() => clampRailWidth(340));
  const railDrag = useRef<{ x: number; width: number } | null>(null);

  useEffect(() => {
    const onResize = () => setRailWidth((current) => clampRailWidth(current));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onRailPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    railDrag.current = { x: event.clientX, width: railWidth };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onRailPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (railDrag.current) {
      setRailWidth(
        clampRailWidth(
          railDrag.current.width + event.clientX - railDrag.current.x,
        ),
      );
    }
  };

  const onRailPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    railDrag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const onRailKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta =
      event.key === 'ArrowLeft' ? -24 : event.key === 'ArrowRight' ? 24 : 0;
    if (delta || event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setRailWidth(
        clampRailWidth(
          event.key === 'Home'
            ? MIN_RAIL_WIDTH
            : event.key === 'End'
              ? MAX_RAIL_WIDTH
              : railWidth + delta,
        ),
      );
    }
  };

  const filteredAgents = useMemo(() => {
    switch (filter) {
      case 'published':
        return agents.filter((a) => latestVersions.get(a.name) !== undefined);
      case 'draft':
        return agents.filter((a) => a.isDraft);
      default:
        return agents;
    }
  }, [agents, filter, latestVersions]);

  // The route param (:agentName) is the single source of truth for the
  // selected agent. When the URL points at no / an unknown / a filtered-out
  // agent, fall back to the first visible row so the center pane always has
  // something to show; an effect then syncs that choice back into the URL.
  const selectedName = useMemo<string | null>(() => {
    if (
      routeAgentName !== null &&
      filteredAgents.some((a) => a.name === routeAgentName)
    ) {
      return routeAgentName;
    }
    return filteredAgents[0]?.name ?? null;
  }, [routeAgentName, filteredAgents]);

  // Keep the URL in step with the effective selection. Because the :agentName
  // child route has no component of its own, this navigation re-matches without
  // remounting AgentsPage. Skipped while creating (the /new outlet owns the
  // pane) or before the list has loaded.
  useEffect(() => {
    if (
      isCreating ||
      isLoading ||
      (filter === 'published' && (versionsLoading || versionsError !== null))
    )
      return;
    if (selectedName === routeAgentName) return;
    const target =
      selectedName === null
        ? basePath
        : `${basePath}/${encodeURIComponent(selectedName)}`;
    void navigate(target, { replace: true });
  }, [
    isCreating,
    isLoading,
    filter,
    versionsLoading,
    versionsError,
    selectedName,
    routeAgentName,
    basePath,
    navigate,
  ]);

  const listSelectedName = isCreating ? null : selectedName;

  // Defer the (heavier) detail render so row highlighting stays snappy.
  const detailSelectedName = useDeferredValue(selectedName);

  const handleRowSelect = useCallback(
    (name: string) => {
      void navigate(`${basePath}/${encodeURIComponent(name)}`, {
        replace: true,
      });
    },
    [navigate, basePath],
  );

  const goToCreate = useCallback(() => {
    void navigate(`${basePath}/new`);
  }, [navigate, basePath]);

  const handleDeleted = useCallback(() => {
    void navigate(basePath, { replace: true });
    refresh();
  }, [navigate, basePath, refresh]);

  const handleChanged = useCallback(() => {
    refresh();
    refreshVersions();
  }, [refresh, refreshVersions]);

  const handleAgentCreated = useCallback(
    (agent: Agent) => {
      upsertAgent(agent);
    },
    [upsertAgent],
  );
  const childOutlet = useOutlet({
    onCreated: handleAgentCreated,
  } satisfies AgentFormOutletContext) as ReactElement | null;

  if (workspacesLoading) {
    return (
      <div className={styles.fullPagePlaceholder}>
        <Spinner label="Loading workspaces..." />
      </div>
    );
  }

  if (workspaceName === null) {
    return (
      <div className={styles.fullPagePlaceholder}>
        <Body1>Pick a workspace from the top bar to view its agents.</Body1>
      </div>
    );
  }

  let listBody: React.ReactNode;
  if (error !== null) {
    const isEntitlementRequired = isDataLakeEntitlementRequiredMessage(error);
    listBody = (
      <div className={styles.fullPagePlaceholder}>
        {isEntitlementRequired ? (
          <DataLakeEntitlementGuidance />
        ) : (
          <Body1 className={styles.errorMessage}>{error}</Body1>
        )}
        <Button onClick={refresh}>Try again</Button>
      </div>
    );
  } else if (filter === 'published' && versionsError !== null) {
    listBody = (
      <div className={styles.fullPagePlaceholder}>
        <Body1 className={styles.errorMessage}>{versionsError}</Body1>
        <Button onClick={refreshVersions}>Try again</Button>
      </div>
    );
  } else if (filter === 'published' && versionsLoading) {
    listBody = (
      <div className={styles.fullPagePlaceholder}>
        <Spinner label="Loading published agents..." />
      </div>
    );
  } else if (agents.length === 0 && !isCreating && !isLoading) {
    listBody = null;
  } else {
    listBody = (
      <div className={styles.list}>
        {versionsError !== null && (
          <div className={styles.versionError} role="alert">
            <Body1 className={styles.errorMessage}>{versionsError}</Body1>
            <Button appearance="subtle" onClick={refreshVersions}>
              Retry
            </Button>
          </div>
        )}
        {isCreating && (
          <div
            className={mergeClasses(styles.row, styles.rowNew)}
            aria-hidden="true"
          >
            <div className={styles.rowNameLine}>
              <span className={styles.rowName}>New agent</span>
              <Badge size="small" appearance="tint" color="brand">
                New
              </Badge>
            </div>
            <span className={styles.rowMeta}>
              <em>Editing…</em>
            </span>
          </div>
        )}
        {filteredAgents.map((a) => {
          const version = latestVersions.get(a.name);
          return (
            <AgentRow
              key={a.name}
              agent={filter === 'published' && version ? version : a}
              version={version?.version}
              draft={filter !== 'published' && a.isDraft}
              selected={a.name === listSelectedName}
              onSelect={handleRowSelect}
            />
          );
        })}
      </div>
    );
  }

  let centerBody: React.ReactNode;
  if (isCreating) {
    centerBody = childOutlet;
  } else if (
    (isLoading && agents.length === 0) ||
    (filter === 'published' && versionsLoading)
  ) {
    centerBody = (
      <div className={styles.fullPagePlaceholder}>
        <Spinner label="Loading agents..." />
      </div>
    );
  } else if (filter === 'published' && versionsError !== null) {
    centerBody = (
      <div className={styles.centerEmpty}>
        <Body1 className={styles.errorMessage}>{versionsError}</Body1>
      </div>
    );
  } else if (error === null && !isLoading && agents.length === 0) {
    centerBody = (
      <div className={styles.centerEmpty}>
        <span className={styles.centerEmptyTitle}>
          No agents in this workspace yet
        </span>
        <span className={styles.centerEmptySub}>
          Create one to start evaluating it against your graders.
        </span>
        <Button appearance="primary" icon={<AddIcon />} onClick={goToCreate}>
          Create Agent
        </Button>
      </div>
    );
  } else if (detailSelectedName === null) {
    centerBody = (
      <div className={styles.centerEmpty}>
        <span className={styles.centerEmptyTitle}>
          {filter === 'published'
            ? 'No published agents yet'
            : 'Select an agent'}
        </span>
        <span className={styles.centerEmptySub}>
          {filter === 'published'
            ? 'Publish a draft to make it available here.'
            : 'Choose an agent from the list, or create a new one to get started.'}
        </span>
      </div>
    );
  } else {
    centerBody = (
      <AgentDetailView
        workspaceName={workspaceName}
        agentName={detailSelectedName}
        publishedVersion={
          filter === 'published'
            ? latestVersions.get(detailSelectedName)
            : undefined
        }
        onDeleted={handleDeleted}
        onChanged={handleChanged}
      />
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.panelsRow}>
        {/* ─── Left pane: list ─────────────────────────────────────────── */}
        <div className={styles.listRail} style={{ width: railWidth }}>
          <div className={styles.leftPanel}>
            {agents.length > 0 && (
              <div className={styles.filterTabs}>
                <TabList
                  size="small"
                  selectedValue={filter}
                  onTabSelect={(_, d) => setFilter(d.value as FilterValue)}
                >
                  <Tab value="all">All</Tab>
                  <Tab value="published">Published</Tab>
                  <Tab value="draft">Draft</Tab>
                </TabList>
              </div>
            )}

            {listBody}

            <div className={styles.leftFooter}>
              <Button
                appearance="primary"
                icon={<AddIcon />}
                className={styles.fullWidthButton}
                onClick={goToCreate}
              >
                Create Agent
              </Button>
            </div>
          </div>
          <div
            className={styles.listRailHandle}
            role="slider"
            aria-label="Resize agent list"
            aria-orientation="horizontal"
            aria-valuemin={MIN_RAIL_WIDTH}
            aria-valuemax={clampRailWidth(MAX_RAIL_WIDTH)}
            aria-valuenow={railWidth}
            tabIndex={0}
            onPointerDown={onRailPointerDown}
            onPointerMove={onRailPointerMove}
            onPointerUp={onRailPointerUp}
            onPointerCancel={onRailPointerUp}
            onKeyDown={onRailKeyDown}
          >
            <span className={styles.listRailGrip} aria-hidden="true">
              ⋮
            </span>
          </div>
        </div>

        {/* ─── Center pane: detail ─────────────────────────────────────── */}
        <div className={styles.centerPanel}>{centerBody}</div>
      </div>
    </div>
  );
}
